import React, { useState } from 'react';
import { BookIcon, CheckSquareIcon, SquareIcon, XIcon, FlameIcon, MoonIcon, BookOpenIcon, ExternalLinkIcon } from 'lucide-react';
import { genreColors, formatIcons } from './constants';
import { BookCard } from './BookCard';
// Mobile bottom sheet shown when tapping a spine on touch devices
const MobileSpineSheet: React.FC<{
  book: any;
  darkMode: boolean;
  onClose: () => void;
  onViewDetails: (book: any) => void;
}> = ({
  book,
  darkMode,
  onClose,
  onViewDetails
}) => {
  const bg = darkMode ? 'bg-gray-900' : 'bg-white';
  const border = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500';
  return <div className="fixed inset-0 z-50 md:hidden" onClick={onClose} data-id="element-11220" data-mp="3qp2js5q77q36">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-11221" data-mp="3vnvxnz9edj3v" />
      <div className={`absolute bottom-0 left-0 right-0 rounded-t-2xl shadow-2xl border-t ${bg} ${border}`} onClick={e => e.stopPropagation()} data-id="element-11222" data-mp="4f8dvnqt23yf">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1" data-id="element-11223" data-mp="2ctz4syn52xz3">
          <div className={`w-10 h-1 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`} data-id="element-11224" data-mp="2cwlfh75bttlj" />
        </div>
        <button onClick={onClose} className={`absolute top-3 right-3 p-2 rounded-full ${darkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'}`} data-id="element-11225" data-mp="23uc4kg65by7m">
          <XIcon className="h-4 w-4" data-id="element-11226" data-mp="2vl9whi0oves1" />
        </button>

        <div className="px-5 pb-6 pt-2" data-id="element-11227" data-mp="2vljkj026bbv3">
          <div className="flex gap-4" data-id="element-11228" data-mp="2qu1l4gfstjc8">
            {/* Cover */}
            <div className="flex-shrink-0 w-20 h-28 rounded-lg overflow-hidden shadow-md" data-id="element-11229" data-mp="2qsgo8k60hob7">
              <img src={book.coverImage} alt={book.title} className="w-full h-full object-cover" data-id="element-11230" data-mp="2msg20dco88tl" />
            </div>
            {/* Info */}
            <div className="flex-1 min-w-0" data-id="element-11231" data-mp="3eikuz0ehy46f">
              <h3 className={`font-bold text-base leading-tight line-clamp-2 mb-0.5 ${textPrimary}`} data-id="element-11232" data-mp="1clsgnc9mrkm1">
                {book.title}
              </h3>
              {(book.series || book.seriesName) && <p className="text-xs text-indigo-500 mb-0.5" data-id="element-11233" data-mp="12vzgr8iuotxm">
                  {book.series || book.seriesName}
                  {book.seriesNumber ? ` #${book.seriesNumber}` : ''}
                </p>}
              <p className={`text-sm mb-2 ${textSecondary}`} data-id="element-11234" data-mp="txx8q90p910u">
                by {book.author}
              </p>

              {/* Genre + subgenre */}
              <div className="flex flex-wrap gap-1 mb-2" data-id="element-11235" data-mp="jyg660gqv393">
                {book.genre && <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'}`} data-id="element-11236" data-mp="ow7y2uhe2pda">
                    {book.genre}
                  </span>}
                {book.subgenre && <span className={`text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-blue-900 text-blue-100' : 'bg-blue-100 text-blue-800'}`} data-id="element-11237" data-mp="7mvw325obeqf">
                    {book.subgenre}
                  </span>}
              </div>

              {/* Spice + darkness */}
              <div className="flex items-center gap-3" data-id="element-11238" data-mp="clguqx5a1qcr">
                {(book.spiceLevel ?? 0) > 0 && <div className="flex gap-0.5" data-id="element-11239" data-mp="qclm7byhrcc0">
                    {Array.from({
                  length: 5
                }).map((_, i) => <FlameIcon key={i} className={`h-3 w-3 ${i < book.spiceLevel ? 'text-red-400 fill-red-400' : darkMode ? 'text-gray-600' : 'text-gray-300'}`} data-id="element-11240" data-mp="10budty5vwev7" data-mpi={i} />)}
                  </div>}
                {(book.darknessLevel ?? 0) > 0 && <div className="flex gap-0.5" data-id="element-11241" data-mp="2x5xlile816sv">
                    {Array.from({
                  length: 5
                }).map((_, i) => <MoonIcon key={i} className={`h-3 w-3 ${i < book.darknessLevel ? 'text-indigo-400 fill-indigo-400' : darkMode ? 'text-gray-600' : 'text-gray-300'}`} data-id="element-11242" data-mp="33k3eexh2had7" data-mpi={i} />)}
                  </div>}
              </div>
            </div>
          </div>

          {/* Format + ending type row */}
          <div className="flex items-center gap-2 mt-3 flex-wrap" data-id="element-11243" data-mp="2ktlr58f076z8">
            {book.format && <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'}`} data-id="element-11244" data-mp="2psoo05kty6w4">
                {formatIcons[book.format]}
                <span className="ml-0.5" data-id="element-11245" data-mp="1x4am3eqmeqfa">{book.format}</span>
              </span>}
            {book.endingType && <span className={`text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-green-900/60 text-green-200' : 'bg-green-100 text-green-800'}`} data-id="element-11246" data-mp="j8izsu0sr81l">
                {book.endingType}
              </span>}
            {book.relationshipType && book.relationshipType !== 'None' && <span className={`text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-pink-900/60 text-pink-200' : 'bg-pink-100 text-pink-800'}`} data-id="element-11247" data-mp="599mc23z6s1b">
                {book.relationshipType}
              </span>}
          </div>

          {/* CTA */}
          <button onClick={() => {
          onViewDetails(book);
          onClose();
        }} className="mt-4 w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm flex items-center justify-center gap-2 transition-colors" data-id="element-11248" data-mp="3nmhhqc4l0k8k">
            <BookOpenIcon className="h-4 w-4" data-id="element-11249" data-mp="38oykeldu8ypi" />
            View My Copy
          </button>
        </div>
      </div>
    </div>;
};
interface StatusTabViewProps {
  darkMode: boolean;
  viewMode: 'spine' | 'cover';
  isEditMode: boolean;
  sortedBooks: any[];
  selectedBooks: string[];
  toggleBookSelection: (id: string) => void;
  navigateToBookDetail: (id: string) => void;
  onCardClick?: (book: any) => void;
  setHoveredBookId: (id: string | null) => void;
  handleDragStart: (id: string) => void;
  handleDragEnter: (id: string) => void;
  handleDragEnd: () => void;
}
export const StatusTabView: React.FC<StatusTabViewProps> = ({
  darkMode,
  viewMode,
  isEditMode,
  sortedBooks,
  selectedBooks,
  toggleBookSelection,
  navigateToBookDetail,
  onCardClick,
  setHoveredBookId,
  handleDragStart,
  handleDragEnter,
  handleDragEnd
}) => {
  const [mobileSheetBook, setMobileSheetBook] = useState<any | null>(null);
  const handleSpineTap = (book: any) => {
    if (isEditMode) {
      toggleBookSelection(book.id);
      return;
    }
    // On mobile, show bottom sheet; on desktop, open card click
    if (window.innerWidth < 768) {
      setMobileSheetBook(book);
    } else if (onCardClick) {
      onCardClick(book);
    } else {
      navigateToBookDetail(book.id);
    }
  };
  return <div data-id="element-11250" data-mp="23shl9ydt6xzb">
      {viewMode === 'spine' ? <div className="relative" data-id="element-11251" data-mp="23rfzayv98r2t">
          <div className="flex items-end gap-2 overflow-x-auto pb-6 scrollbar-thin" style={{
        minHeight: 260
      }} data-id="element-11252" data-mp="23ulkw44odats">
            {sortedBooks.map(book => {
          const spineColor = !book.coverImage ? genreColors[book.genre] || genreColors.default : undefined;
          return <div key={book.id} draggable={isEditMode} onDragStart={() => isEditMode && handleDragStart(book.id)} onDragEnter={() => isEditMode && handleDragEnter(book.id)} onDragEnd={() => isEditMode && handleDragEnd()} onDragOver={e => isEditMode && e.preventDefault()} onClick={() => handleSpineTap(book)} onMouseEnter={() => !isEditMode && setHoveredBookId(book.id)} onMouseLeave={() => !isEditMode && setHoveredBookId(null)} className={`relative flex flex-col items-center justify-center text-white font-medium rounded transition-transform duration-200 ease-in-out cursor-pointer text-center ${!isEditMode && 'hover:scale-105'} select-none`} style={{
            width: book.edition === 'special' ? 68 : 56,
            height: book.edition === 'special' ? 250 : 220,
            fontSize: book.edition === 'special' ? '1rem' : '0.85rem',
            backgroundColor: spineColor,
            backgroundImage: book.coverImage ? `url(${book.coverImage})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            flexShrink: 0,
            marginBottom: 4
          }} title={`${book.title}`} data-id="element-11253" data-mp="1l06lweid5i5y">
                  {!book.coverImage && <div className="flex flex-col items-center px-1 w-full h-full justify-center" style={{
              writingMode: 'vertical-rl'
            }} data-id="element-11254" data-mp="370o78oxwlhoe">
                      <div className="w-full truncate" data-id="element-11255" data-mp="3io53qf146ip">{book.title}</div>
                      {book.seriesNumber && <div className="mt-2 text-xs font-normal opacity-80" data-id="element-11256" data-mp="3prytutwlj8ah">
                          #{book.seriesNumber}
                        </div>}
                    </div>}
                  <div className="absolute bottom-1 left-1 inline-flex items-center justify-center p-1 rounded bg-black bg-opacity-70 text-white text-xs z-10" title={book.format} data-id="element-11257" data-mp="2edblui3hbfwo">
                    {formatIcons[book.format]}
                  </div>
                  {isEditMode && <div className="absolute top-1 right-1 z-20" data-id="element-11258" data-mp="3fx3fa2yn5rem">
                      {selectedBooks.includes(book.id) ? <CheckSquareIcon className="h-5 w-5 text-indigo-500 bg-white bg-opacity-90 rounded" data-id="element-11259" data-mp="3ptg9d1y3m79w" /> : <SquareIcon className="h-5 w-5 text-white bg-black bg-opacity-50 rounded" data-id="element-11260" data-mp="7efga24ux56s" />}
                    </div>}
                </div>;
        })}
            <div className={`absolute bottom-0 left-0 right-0 h-2 ${darkMode ? 'bg-indigo-900' : 'bg-indigo-500'} rounded-full`} data-id="element-11261" data-mp="hd4n8zv7t589" />
          </div>
        </div> : <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-6" data-id="element-11262" data-mp="2j5je4ofqlk0t">
          {sortedBooks.map(book => <BookCard key={book.id} book={book} isEditMode={isEditMode} toggleBookSelection={toggleBookSelection} navigateToBookDetail={navigateToBookDetail} onCardClick={onCardClick} setHoveredBookId={setHoveredBookId} selectedBooks={selectedBooks} darkMode={darkMode} handleDragStart={handleDragStart} handleDragEnter={handleDragEnter} handleDragEnd={handleDragEnd} data-id="element-11263" data-mp="tnvv9ai9usf4" />)}
        </div>}

      {sortedBooks.length === 0 && <div className={`text-center py-12 ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-md`} data-id="element-11264" data-mp="zqfl262rtm5">
          <BookIcon className={`h-12 w-12 ${darkMode ? 'text-gray-600' : 'text-gray-400'} mx-auto mb-4`} data-id="element-11265" data-mp="5z30vihassmm" />
          <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-900'}`} data-id="element-11266" data-mp="1vb563l6wtx2y">
            No books found
          </h3>
          <p className={darkMode ? 'text-gray-400' : 'text-gray-500'} data-id="element-11267" data-mp="1yvyh6edvx0za">
            Try adjusting your search or filters
          </p>
        </div>}

      {/* Mobile spine bottom sheet */}
      {mobileSheetBook && <MobileSpineSheet book={mobileSheetBook} darkMode={darkMode} onClose={() => setMobileSheetBook(null)} onViewDetails={book => {
      onCardClick ? onCardClick(book) : navigateToBookDetail(book.id);
    }} data-id="element-11268" data-mp="1oz27i5c928t5" />}
    </div>;
};